import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { Wordmark } from "../brand";
import { NAV_LINKS, MARKETS_LIST } from "../../content/site";

export default function PublicHeader() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [marketsOpen, setMarketsOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled ? "glass-nav border-b border-altura-espresso/10" : "bg-altura-ivory/0"}`}
      data-testid="public-header"
    >
      <div className="container-editorial flex items-center justify-between py-3 gap-4">
        <Link to="/" className="flex items-center shrink-0" data-testid="brand-home-link" aria-label="Altura Global Real Estate — Home">
          <Wordmark variant="text" height={scrolled ? 40 : 52} />
        </Link>

        <nav className="hidden xl:flex items-center gap-6 2xl:gap-9">
          {NAV_LINKS.map((n) => n.dropdown ? (
            <div key={n.label} className="relative"
                 onMouseEnter={() => setMarketsOpen(true)}
                 onMouseLeave={() => setMarketsOpen(false)}>
              <button className={`link-underline flex items-center gap-1 whitespace-nowrap text-[13px] font-medium tracking-wide text-altura-espresso/90 hover:text-altura-espresso`}
                      data-testid={`nav-${n.label.toLowerCase()}`}>
                {n.label} <ChevronDown className="h-3.5 w-3.5" />
              </button>
              {marketsOpen && (
                <div className="absolute left-0 top-full pt-3">
                  <div className="w-56 rounded-md border border-altura-espresso/10 bg-white p-2 shadow-lg">
                    {MARKETS_LIST.map((m) => (
                      <NavLink key={m.slug} to={`/markets/${m.slug}`}
                               className="flex items-center justify-between rounded px-3 py-2 text-sm text-altura-espresso hover:bg-altura-ivory-2"
                               data-testid={`nav-market-${m.slug}`}>
                        <span>{m.flag} <span className="ml-1.5">{m.name}</span></span>
                        {m.is_flagship && <span className="badge-gold text-[9px]">Flagship</span>}
                      </NavLink>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <NavLink key={n.label} to={n.to}
                     className={({ isActive }) => `link-underline whitespace-nowrap text-[13px] font-medium tracking-wide ${isActive ? "text-altura-espresso" : "text-altura-espresso/80 hover:text-altura-espresso"}`}
                     data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}>
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden xl:flex items-center gap-2 2xl:gap-3 shrink-0">
          <Link to="/explore-ghana" className="btn-outline-gold btn-compact whitespace-nowrap" data-testid="cta-explore-ghana-header">Explore Ghana</Link>
          <Link to="/contact" className="btn-gold btn-compact whitespace-nowrap" data-testid="cta-request-info-header">Request Information</Link>
        </div>

        <button className="xl:hidden text-altura-espresso" onClick={() => setOpen(true)} data-testid="nav-mobile-open" aria-label="Open menu">
          <Menu className="h-6 w-6" />
        </button>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-white text-altura-charcoal xl:hidden" data-testid="mobile-nav">
          <div className="flex items-center justify-between px-6 py-4 border-b border-altura-gray-line">
            <Wordmark variant="text" height={44} />
            <button onClick={() => setOpen(false)} aria-label="Close menu" data-testid="nav-mobile-close" className="text-altura-charcoal"><X className="h-6 w-6" /></button>
          </div>
          <nav className="px-6 py-6 flex flex-col gap-3">
            {NAV_LINKS.map((n) => (
              <NavLink key={n.label} to={n.to} onClick={() => setOpen(false)}
                       className="text-lg font-editorial py-2 border-b border-altura-gray-line text-altura-charcoal">{n.label}</NavLink>
            ))}
            <div className="pt-4 flex flex-col gap-3">
              <Link to="/explore-ghana" onClick={() => setOpen(false)} className="btn-outline-gold w-full justify-center">Explore Ghana</Link>
              <Link to="/contact" onClick={() => setOpen(false)} className="btn-gold w-full justify-center">Request Information</Link>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
